<template>
  <div id="pushCenter">
    <div class="pc-wrap">

      <!-- 헤더 -->
      <header class="pc-head">
        <h3>알림센터</h3>
        <p class="pc-summary" v-if="!loading && user">
          전체 <strong>{{ getCurrencyFormat(pushList.length) }}</strong>건
          <span class="pc-dot-sep">·</span>
          읽지 않음 <strong class="pc-unread-cnt">{{ getCurrencyFormat(unreadCount) }}</strong>건
        </p>
      </header>

      <!-- 로딩 -->
      <ul class="pc-list" v-if="loading">
        <li class="pc-item pc-skeleton" v-for="n in 4" :key="'sk' + n">
          <span class="sk-icon"></span>
          <span class="sk-lines"><i></i><i></i></span>
        </li>
      </ul>

      <!-- 비로그인 / 빈 목록 -->
      <div class="pc-empty" v-else-if="!user || pushList.length === 0">
        <i class="bi bi-bell-slash"></i>
        <p v-if="!user">로그인 후 알림을 확인하실 수 있습니다.</p>
        <p v-else>받은 알림이 없습니다.</p>
      </div>

      <!-- 목록 -->
      <ul class="pc-list" v-else>
        <li
          v-for="push in pagedList"
          :key="itemKey(push)"
          class="pc-item"
          :class="{ 'is-unread': !push.checkDate, 'is-open': openedKey === itemKey(push) }"
        >
          <button type="button" class="pc-row" @click="toggleItem(push)" :aria-expanded="openedKey === itemKey(push)">
            <span class="pc-icon">
              <i :class="push.checkDate ? 'bi bi-bell' : 'bi bi-bell-fill'"></i>
            </span>

            <span class="pc-main">
              <span class="pc-title">
                {{ push.puTitle }}
                <span class="pc-new" v-if="!push.checkDate">N</span>
              </span>
              <span class="pc-date">{{ getDateFormat(push.puDate) }}</span>
            </span>

            <i class="bi bi-chevron-down pc-chevron"></i>
          </button>

          <!-- 푸시 내용 -->
          <div class="pc-body" v-show="openedKey === itemKey(push)">
            <pre>{{ push.puContent }}</pre>
          </div>
        </li>
      </ul>

      <!-- 페이지네이션 -->
      <nav class="pc-paging" v-if="!loading && totalPage > 1" aria-label="Page navigation">
        <button type="button" class="pg-btn" :disabled="page === 1" @click="goPage(page - 1)" aria-label="이전">
          <i class="bi bi-chevron-left"></i>
        </button>
        <button
          type="button"
          v-for="p in pageNumbers"
          :key="'p' + p"
          class="pg-btn"
          :class="{ active: p === page }"
          @click="goPage(p)"
        >{{ p }}</button>
        <button type="button" class="pg-btn" :disabled="page === totalPage" @click="goPage(page + 1)" aria-label="다음">
          <i class="bi bi-chevron-right"></i>
        </button>
      </nav>

    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      user: "",
      pushList: [],        // 개인+전체 푸시 목록 (selPushHistory)
      openedKey: null,     // 펼쳐진 항목 key
      readingKeys: {},     // 읽음처리 중인 항목 (중복클릭 방지)
      loading: false,
      page: 1,
      pageSize: 12,
      pageWindow: 5        // 한 번에 보여줄 페이지 번호 개수
    };
  },

  computed: {
    unreadCount() {
      return this.pushList.filter(p => !p.checkDate).length;
    },
    totalPage() {
      return Math.max(1, Math.ceil(this.pushList.length / this.pageSize));
    },
    pagedList() {
      const offset = (this.page - 1) * this.pageSize;
      return this.pushList.slice(offset, offset + this.pageSize);
    },
    pageNumbers() {
      const half = Math.floor(this.pageWindow / 2);
      let start = Math.max(1, this.page - half);
      let end = Math.min(this.totalPage, start + this.pageWindow - 1);
      start = Math.max(1, end - this.pageWindow + 1);
      const arr = [];
      for (let i = start; i <= end; i++) arr.push(i);
      return arr;
    }
  },

  created() {
    const st = this.$store.state.user || {};
    this.user = st.handlerId || st.email || "";

    if (this.user) {
      this.getNoticeList();
    }
  },

  methods: {
    getCurrencyFormat(value) {
      return this.$currencyFormat(Number(value) || 0);
    },
    getDateFormat(date) {
      return this.$dateFormat(date);
    },

    // 개별푸시(PE)는 puSeq, 전체푸시(AL)는 puCode 기준으로 고유키 생성
    itemKey(push) {
      return `${push.puStyle}-${push.puSeq || push.puCode}`;
    },

    async getNoticeList() {
      this.loading = true;
      try {
        const res = await this.$api("/api/selPushHistory", { param: [this.user] });
        if (Array.isArray(res)) {
          this.pushList = res;
        } else {
          // $api / req.db 는 에러 시 throw 없이 {error} 객체를 반환함
          console.error("selPushHistory error:", res && res.error);
          this.pushList = [];
        }
      } finally {
        this.loading = false;
        if (this.page > this.totalPage) this.page = this.totalPage;
      }
    },

    toggleItem(push) {
      const key = this.itemKey(push);
      this.openedKey = this.openedKey === key ? null : key;

      // 열 때, 읽지 않은 항목만 읽음처리
      if (this.openedKey === key && !push.checkDate) {
        this.markRead(push, key);
      }
    },

    async markRead(push, key) {
      if (this.readingKeys[key]) return;
      this.readingKeys = { ...this.readingKeys, [key]: true };

      try {
        let res = null;
        if (push.puStyle === "PE") {
          res = await this.$api("/api/updPushRead", { param: [this.user, push.puSeq] });
        } else if (push.puStyle === "AL") {
          res = await this.$api("/api/updPush_all_Read", { param: [push.puCode, this.user] });
        }

        if (res && !res.error && res.affectedRows > 0) {
          // 재조회 없이 로컬 상태만 갱신 → 즉시 'N' 배지 제거
          push.checkDate = new Date();
        } else {
          console.warn("푸시 확인처리 실패:", res && res.error);
        }
      } finally {
        const next = { ...this.readingKeys };
        delete next[key];
        this.readingKeys = next;
      }
    },

    goPage(p) {
      if (p < 1 || p > this.totalPage || p === this.page) return;
      this.page = p;
      this.openedKey = null;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
};
</script>

<style scoped>
#pushCenter {
  --pc-black: #111;
  --pc-text: #333;
  --pc-sub: #8a8a8a;
  --pc-line: #ececec;
  --pc-bg-soft: #f7f7f7;
  --pc-accent: #8fcd53;
  --pc-accent-soft: #f1f9e9;
  --pc-new: #ee4bcb;

  margin: 4rem 0 8rem;
  color: var(--pc-text);
}
@media (max-width: 992px) {
  #pushCenter { margin: 5rem 0 4rem; }
}

.pc-wrap {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
}

/* ---------- 헤더 ---------- */
.pc-head {
  text-align: center;
  margin-bottom: 1.75rem;
}
.pc-head h3 {
  color: #000;
  font-weight: 600;
  font-size: 1.8rem;
  letter-spacing: 0.3em;
  margin: 0 0 0.6rem;
  padding-left: 0.3em; /* letter-spacing 좌우 균형 */
}
.pc-summary {
  margin: 0;
  font-size: 0.9rem;
  color: var(--pc-sub);
}
.pc-summary strong { color: var(--pc-black); font-weight: 600; }
.pc-summary .pc-unread-cnt { color: var(--pc-new); }
.pc-dot-sep { margin: 0 6px; }

/* ---------- 목록 ---------- */
.pc-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 2px solid #000;
}
.pc-item {
  border-bottom: 1px solid var(--pc-line);
  background: #fff;
  transition: background-color 0.15s;
}
.pc-item.is-unread { background: #fff; }
.pc-item.is-open { background: var(--pc-bg-soft); }

.pc-row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 12px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pc-row:active { background: rgba(0, 0, 0, 0.03); }

.pc-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--pc-bg-soft);
  color: #b5b5b5;
  font-size: 1.1rem;
}
.is-unread .pc-icon {
  background: var(--pc-accent-soft);
  color: var(--pc-accent);
}

.pc-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pc-title {
  font-size: 1rem;
  color: #666;
  line-height: 1.4;
  word-break: keep-all;
  overflow-wrap: anywhere;
}
.is-unread .pc-title {
  color: var(--pc-black);
  font-weight: 600;
}
.pc-new {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  border-radius: 50%;
  background: var(--pc-new);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  vertical-align: 2px;
}
.pc-date {
  font-size: 0.8rem;
  color: var(--pc-sub);
}

.pc-chevron {
  flex: 0 0 auto;
  color: #bbb;
  font-size: 0.9rem;
  transition: transform 0.2s;
}
.is-open .pc-chevron { transform: rotate(180deg); color: var(--pc-black); }

/* 내용 */
.pc-body {
  padding: 0 12px 18px 66px; /* 아이콘 폭만큼 들여쓰기 */
}
.pc-body pre {
  margin: 0;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid var(--pc-line);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.92rem;
  line-height: 1.65;
  color: var(--pc-text);
  white-space: pre-wrap;
  word-break: break-word;
}

/* ---------- 빈 상태 ---------- */
.pc-empty {
  border-top: 2px solid #000;
  padding: 4.5rem 0;
  text-align: center;
  color: var(--pc-sub);
}
.pc-empty i { font-size: 2.4rem; color: #d0d0d0; }
.pc-empty p { margin: 0.8rem 0 0; font-size: 0.95rem; }

/* ---------- 스켈레톤 ---------- */
.pc-skeleton {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 12px;
}
.sk-icon {
  width: 40px; height: 40px; border-radius: 50%;
  background: #eee;
}
.sk-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; }
.sk-lines i { display: block; height: 12px; border-radius: 6px; background: #eee; }
.sk-lines i:first-child { width: 70%; }
.sk-lines i:last-child { width: 30%; }
.pc-skeleton .sk-icon, .pc-skeleton .sk-lines i {
  animation: pcPulse 1.2s ease-in-out infinite;
}
@keyframes pcPulse { 50% { opacity: 0.45; } }

/* ---------- 페이지네이션 ---------- */
.pc-paging {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 2rem;
}
.pg-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 6px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #555;
  font-size: 0.9rem;
  cursor: pointer;
}
.pg-btn.active {
  background: #000;
  color: #fff;
  font-weight: 600;
}
.pg-btn:disabled { color: #ccc; cursor: default; }

/* ---------- 모바일 ---------- */
@media (max-width: 576px) {
  .pc-head h3 { font-size: 1.5rem; }
  .pc-row { padding: 14px 4px; gap: 12px; }
  .pc-icon { flex-basis: 36px; width: 36px; height: 36px; font-size: 1rem; }
  .pc-body { padding: 0 4px 16px; }
  .pc-title { font-size: 0.95rem; }
}
</style>